/**
 * components/brief-builder/BriefIntakeStep.tsx
 *
 * Step 1 — User enters the client brief.
 * Roles: Account Manager
 */
import { useState } from 'react'
import type { CreateSessionPayload } from '@/types/brief-builder'

interface Props {
  onSubmit: (payload: CreateSessionPayload) => void
  loading?: boolean
}

// ── Test prefills ──────────────────────────────────────────────────────────────
const TEST_PREFILLS: Record<string, {
  tenantName: string; website: string; industry: string; markets: string
  stakeholders: string; goals: string; brief: string; notes: string
}> = {
  'BNP Paribas – Investment Banking': {
    tenantName:   'BNP Paribas',
    website:      'https://cib.bnpparibas.com',
    industry:     'Investment Banking',
    markets:      'FR, UK, DE, BE, LU',
    stakeholders: 'Head of Strategy, Chief Marketing Officer, Head of Client Intelligence, ' +
                  'Research Analysts, Business Development Directors, DCM/ECM Coverage Bankers',
    goals:
      'Monitor competitor product launches and capital market deal activity. ' +
      'Track regulatory and macro-economic shifts across EU markets. ' +
      'Identify cross-sell and new-mandate opportunities within the existing client portfolio. ' +
      'Understand CFO/Treasurer sentiment and emerging treasury needs. ' +
      'Benchmark our league-table positioning vs Société Générale, Deutsche Bank, Barclays, JPMorgan.',
    brief:
      'BNP Paribas Corporate & Institutional Banking is looking to deploy an intelligence ' +
      'platform for its coverage and strategy teams across Western Europe. ' +
      'The primary use case is competitive intelligence: we need real-time visibility into ' +
      'what our key competitors (SocGen, DB, Barclays, JPM) are doing in DCM, ECM, and M&A advisory. ' +
      'Secondary use case is client intelligence — our relationship managers need a feed of news ' +
      'and signals about their client accounts (earnings surprises, rating changes, CFO turnover, ' +
      'regulatory filings). ' +
      'We also want to track macro themes: ESG regulation (CSRD, EU Taxonomy), rate environment, ' +
      'and sector-specific trends in Energy Transition and Infrastructure Finance. ' +
      'The platform should support our Paris, London, Frankfurt, Brussels, and Luxembourg offices. ' +
      'Users will range from senior coverage bankers to junior analysts — we need role-based views. ' +
      'Ideally integrated with our existing CRM (Salesforce) and internal research portal.',
    notes:
      'Meeting with Head of Strategy (Paris HQ) + CIB CMO on 5 March 2026. ' +
      'Key pain points discussed: (1) analysts spend 3–4 hrs/day manually aggregating news from ' +
      'Bloomberg, Reuters, and internal systems; (2) no single view of what competitors announced ' +
      'this week; (3) client-facing RMs have no structured alert system for account triggers. ' +
      'Budget: €400k–600k/year platform licence. Decision timeline: Q2 2026 pilot, Q3 full rollout. ' +
      'IT security requirement: EU data residency mandatory (GDPR).',
  },
}

export default function BriefIntakeStep({ onSubmit, loading }: Props) {
  const [tenantName,      setTenantName]      = useState('')
  const [website,         setWebsite]         = useState('')
  const [industry,        setIndustry]        = useState('')
  const [rawBrief,        setRawBrief]        = useState('')
  const [stakeholdersRaw, setStakeholdersRaw] = useState('')
  const [marketsRaw,      setMarketsRaw]      = useState('')
  const [languagesRaw,    setLanguagesRaw]    = useState('')
  const [goalsRaw,        setGoalsRaw]        = useState('')
  const [notes,           setNotes]           = useState('')

  const applyPrefill = (key: string) => {
    const p = TEST_PREFILLS[key]
    if (!p) return
    setTenantName(p.tenantName)
    setWebsite(p.website)
    setIndustry(p.industry)
    setMarketsRaw(p.markets)
    setStakeholdersRaw(p.stakeholders)
    setGoalsRaw(p.goals)
    setRawBrief(p.brief)
    setNotes(p.notes)
    setLanguagesRaw('English')
  }

  const canSubmit = tenantName.trim() && website.trim() && industry.trim() && rawBrief.trim().length >= 20

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({
      tenant_name:      tenantName.trim(),
      website:          website.trim(),
      industry:         industry.trim(),
      raw_brief:        rawBrief.trim(),
      stakeholders_raw: stakeholdersRaw.trim(),
      markets:          marketsRaw.split(',').map(m => m.trim()).filter(Boolean),
      languages:        languagesRaw.split(',').map(l => l.trim()).filter(Boolean),
      goals_raw:        goalsRaw.trim(),
      uploaded_notes:   notes.trim() || undefined,
    })
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-8">
      <div>
        <h2 className="text-xl font-bold text-slate-900">New Client Brief</h2>
        <p className="text-sm text-slate-500 mt-1">
          Paste the unstructured brief and key context. The AI will extract structured requirements.
        </p>
      </div>

      {/* Dev prefill toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
          🧪 Test prefill
        </span>
        {Object.keys(TEST_PREFILLS).map(key => (
          <button
            key={key}
            type="button"
            onClick={() => applyPrefill(key)}
            className="px-3 py-1 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800
                       text-xs font-medium border border-amber-300 transition-colors"
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setTenantName(''); setWebsite(''); setIndustry(''); setMarketsRaw('')
            setLanguagesRaw('')
            setStakeholdersRaw(''); setGoalsRaw(''); setRawBrief(''); setNotes('')
          }}
          className="ml-auto px-3 py-1 rounded-md text-xs text-slate-500 hover:text-slate-700
                     hover:bg-slate-100 transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Tenant details */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Client Details
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Client / Tenant Name <span className="text-red-500">*</span></span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="e.g. Acme Pharma"
              value={tenantName}
              onChange={e => setTenantName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Website URL <span className="text-red-500">*</span></span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="https://www.client.com"
              type="url"
              value={website}
              onChange={e => setWebsite(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Industry <span className="text-red-500">*</span></span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="e.g. Pharmaceuticals, Insurance, FMCG"
              value={industry}
              onChange={e => setIndustry(e.target.value)}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Markets</span>
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-300"
            placeholder="Comma-separated, e.g. DE, FR, UK"
            value={marketsRaw}
            onChange={e => setMarketsRaw(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Languages
            <span className="ml-1 text-xs font-normal text-slate-400">(used by AI features)</span>
          </span>
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-300"
            placeholder="Comma-separated, e.g. English, French, Thai"
            value={languagesRaw}
            onChange={e => setLanguagesRaw(e.target.value)}
          />
        </label>
      </section>

      {/* Brief */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Brief <span className="text-red-500">*</span>
        </h3>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm h-48
                     focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
          placeholder="Paste the client brief, meeting summary, or project description here…"
          value={rawBrief}
          onChange={e => setRawBrief(e.target.value)}
        />
      </section>

      {/* Stakeholders */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Stakeholders
        </h3>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm h-24
                     focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
          placeholder="Describe who will use the platform and their roles…"
          value={stakeholdersRaw}
          onChange={e => setStakeholdersRaw(e.target.value)}
        />
      </section>

      {/* Goals */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Goals
        </h3>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm h-24
                     focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
          placeholder="Key business goals or outcomes the client wants to achieve…"
          value={goalsRaw}
          onChange={e => setGoalsRaw(e.target.value)}
        />
      </section>

      {/* Meeting notes */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Meeting Notes / Transcript
          <span className="ml-2 text-xs font-normal text-slate-400 normal-case">(optional)</span>
        </h3>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm h-32
                     focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y"
          placeholder="Paste any additional meeting notes or transcript…"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </section>

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                     hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed
                     transition-colors"
        >
          {loading ? 'Running AI interpretation…' : 'Interpret Brief →'}
        </button>
      </div>
    </div>
  )
}
